import { useEffect, useRef, useState } from "react";
import { useAuthStore } from "../../../stores/useAuthStore";
import { isAxiosError } from "axios";
import { refreshSession } from "../../../services/apiClient";

const MIN_BACKOFF_MS = 1_000;
const MAX_BACKOFF_MS = 15_000;

interface StreamOptions {
  /** Sends the JWT (owner stream). Friend streams are keyed by the URL token. */
  auth: boolean;
  onRevision: (revision: string) => void;
  /** The server ended the stream for good (link revoked / expired / bill gone). */
  onClosed?: () => void;
}

/**
 * Minimal SSE reader over fetch: EventSource cannot send an Authorization
 * header, which the owner stream needs. Reconnects with backoff and pauses
 * while the tab is hidden. Reports whether the stream is currently connected.
 */
export function useBillEventStream(
  url: string | null,
  { auth, onRevision, onClosed }: StreamOptions,
): boolean {
  const [live, setLive] = useState(false);
  // Latest callbacks without re-opening the stream on every render.
  const handlers = useRef({ onRevision, onClosed });
  useEffect(() => {
    handlers.current = { onRevision, onClosed };
  });

  useEffect(() => {
    if (!url) return;
    let stopped = false;
    let controller: AbortController | null = null;
    let retry: ReturnType<typeof setTimeout> | undefined;
    let backoff = MIN_BACKOFF_MS;

    const schedule = () => {
      if (stopped || document.hidden) return;
      retry = setTimeout(connect, backoff);
      backoff = Math.min(backoff * 2, MAX_BACKOFF_MS);
    };

    async function connect() {
      if (stopped || document.hidden) return;
      const mine = new AbortController();
      controller = mine;
      try {
        const headers: Record<string, string> = { Accept: "text/event-stream" };
        const token = useAuthStore.getState().accessToken;
        if (auth && token) headers.Authorization = `Bearer ${token}`;

        let res = await fetch(url!, { headers, signal: mine.signal });
        if (res.status === 401 && auth) {
          let accessToken: string;
          try {
            accessToken = await refreshSession();
          } catch (err) {
            // Session is dead (refreshSession already sent us to /login): retrying would loop.
            if (isAxiosError(err) && err.response?.status === 401) {
              stopped = true;
              return;
            }
            throw err;
          }
          headers.Authorization = `Bearer ${accessToken}`;
          res = await fetch(url!, { headers, signal: mine.signal });
        }
        if (res.status === 404) {
          handlers.current.onClosed?.();
          return;
        }
        if (!res.ok || !res.body) throw new Error(String(res.status));

        setLive(true);
        backoff = MIN_BACKOFF_MS;
        const reader = res.body.pipeThrough(new TextDecoderStream()).getReader();
        let buffer = "";
        for (;;) {
          const { value, done } = await reader.read();
          if (done) break;
          buffer += value;
          let end: number;
          while ((end = buffer.indexOf("\n\n")) !== -1) {
            const frame = buffer.slice(0, end);
            buffer = buffer.slice(end + 2);
            dispatch(frame);
          }
        }
      } catch {
        // network drop or abort: fall through to the reconnect below
      }
      setLive(false);
      // Aborted on purpose (hidden tab, reconnect, unmount): whoever aborted decides.
      if (!mine.signal.aborted) schedule();
    }

    function dispatch(frame: string) {
      let type = "message";
      let data = "";
      for (const line of frame.split("\n")) {
        if (line.startsWith("event:")) type = line.slice(6).trim();
        else if (line.startsWith("data:")) data += line.slice(5).trim();
      }
      if (type === "closed") {
        stopped = true;
        handlers.current.onClosed?.();
      } else if (type === "revision") {
        try {
          const { revision } = JSON.parse(data) as { revision: string };
          handlers.current.onRevision(revision);
        } catch {
          // malformed frame: ignore
        }
      }
    }

    const onVisibility = () => {
      if (document.hidden) {
        controller?.abort();
        clearTimeout(retry);
      } else if (!stopped) {
        backoff = MIN_BACKOFF_MS;
        void connect();
      }
    };
    const onOnline = () => {
      if (stopped || document.hidden) return;
      controller?.abort();
      clearTimeout(retry);
      backoff = MIN_BACKOFF_MS;
      void connect();
    };

    void connect();
    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("online", onOnline);
    return () => {
      stopped = true;
      controller?.abort();
      clearTimeout(retry);
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("online", onOnline);
      setLive(false);
    };
  }, [url, auth]);

  return live;
}
